import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  MessageCircle, Video, BookOpen, Users, Wind, Shield, 
  ChevronRight, Bell, Settings, LogOut, Menu, X,
  CheckCircle, Clock, AlertCircle, Lock
} from "lucide-react";
import Layout from "../components/Layout";
import TopicOfMonth from "../components/TopicOfMonth";
import EditableText from "../components/EditableText";
import FoundingMemberBanner from "../components/FoundingMemberBanner";
import MyRegulatorCard from "../components/MyRegulatorCard";

const Dashboard = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    unreadMessages: 0,
    recentDiscussions: [],
    upcomingEvents: []
  });
  const [loading, setLoading] = useState(true);

  // Pending-approval users (just registered, awaiting admin verification) get a
  // tightly-locked dashboard. Their 30-day trial clock has NOT started yet —
  // it begins the moment admin approves their submission. Only Box Breathing
  // and Verification are unlocked.
  const isPending = !user?.is_admin &&
    (user?.verification_status !== "verified" || user?.membership_status === "pending_approval");
  const PENDING_UNLOCKED = new Set(["Breathing", "Verification"]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      // Pending users can't load gated content — skip and hide those sections.
      if (isPending) {
        setLoading(false);
        return;
      }
      try {
        const [unreadRes, discussionsRes, videoRes] = await Promise.all([
          axios.get(`${API}/messages/unread-count`),
          axios.get(`${API}/discussions?limit=5`),
          axios.get(`${API}/video/rooms?status=scheduled`)
        ]);
        
        setStats({
          unreadMessages: unreadRes.data.count,
          recentDiscussions: discussionsRes.data.slice(0, 3),
          upcomingEvents: videoRes.data.slice(0, 3)
        });
      } catch (error) {
        console.error("Failed to fetch dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [isPending]);

  const getVerificationBadge = () => {
    const status = user?.verification_status;
    if (status === "verified") {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#10B981] text-white">
          <CheckCircle className="w-3 h-3 mr-1" />
          Verified
        </span>
      );
    }
    if (status === "submitted") {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#8DB5A0] text-white">
          <Clock className="w-3 h-3 mr-1" />
          Under Review
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#8DB5A0] text-[#6B4E8A]">
        <AlertCircle className="w-3 h-3 mr-1" />
        Pending Verification
      </span>
    );
  };

  const quickActions = [
    { icon: MessageCircle, label: "Discussions", path: "/discussions", color: "#8DB5A0" },
    { icon: Users, label: "Messages", path: "/messages", color: "#6B4E8A", badge: stats.unreadMessages },
    { icon: Video, label: "Video Calls", path: "/video", color: "#8DB5A0" },
    { icon: BookOpen, label: "Resources", path: "/resources", color: "#10B981" },
    { icon: Wind, label: "Breathing", path: "/breathing", color: "#F0ECF7" },
    { icon: Shield, label: "Verification", path: "/verification", color: "#6B4E8A" },
  ];

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="dashboard">
        {/* Phase 64.F: Founding / Lifetime banner — renders only for matched member types */}
        <FoundingMemberBanner />

        {/* Welcome Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <EditableText
                  blockKey="dashboard.welcome_greeting"
                  fallback="Welcome back,"
                  as="span"
                />{" "}
                {user?.name?.split(' ')[0] || 'there'}!
              </h1>
              <div className="flex items-center gap-3">
                {getVerificationBadge()}
                {user?.province && (
                  <span className="text-sm text-[#64748B]">{user.province}</span>
                )}
              </div>
            </div>
            {user?.verification_status !== "verified" && (
              <Button 
                onClick={() => navigate("/verification")}
                className="bg-[#8DB5A0] text-[#6B4E8A] hover:bg-[#C9A432] rounded-full"
                data-testid="verify-cta-btn"
              >
                Complete Verification
              </Button>
            )}
          </div>
        </motion.div>

        {/* Topic of the Month — hidden while pending */}
        {!isPending && <TopicOfMonth isAdmin={user?.is_admin} compact={false} />}

        {/* Pending-approval banner — replaces the old plain verification banner with
            clearer "you're locked until we approve" copy + 30-day trial promise */}
        {isPending && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-[#6B4E8A] to-[#553D6F] rounded-2xl p-6 mb-8 text-white"
            data-testid="pending-approval-banner"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                <Clock className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-lg mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {user?.verification_status === "submitted" ? "Awaiting Admin Approval" : "Submit Your Verification"}
                </h3>
                <p className="text-white/90 text-sm mb-3 leading-relaxed">
                  {user?.verification_status === "submitted"
                    ? "Thanks for submitting! We're reviewing your credentials with the provincial registry. You'll get an email the moment you're approved — your 30-day free trial starts then, not now."
                    : "Upload your Government Photo ID and Active Practice Permit to unlock the community. Your 30-day free trial doesn't start until our team verifies your registration — so you get a full month from the moment you're approved."}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  {user?.verification_status !== "submitted" && (
                    <Button
                      onClick={() => navigate("/verification")}
                      className="bg-white text-[#6B4E8A] hover:bg-white/90 rounded-full text-sm"
                      data-testid="pending-verify-cta"
                    >
                      Submit Verification
                    </Button>
                  )}
                  <Button
                    onClick={() => navigate("/breathing")}
                    variant="outline"
                    className="border-white/30 bg-transparent text-white hover:bg-white/10 rounded-full text-sm"
                    data-testid="pending-breathe-cta"
                  >
                    <Wind className="w-4 h-4 mr-1" /> Try Box Breathing
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Verification Banner (legacy — only for users who somehow bypass pending state) */}
        {!isPending && user?.verification_status === "pending" && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-r from-[#8DB5A0] to-[#8DB5A0] rounded-2xl p-6 mb-8 text-white"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                <Shield className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-lg mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Verify Your Registration
                </h3>
                <p className="text-white/90 text-sm mb-3">
                  Submit your dental hygienist credentials to access all community features including discussions and video calls.
                </p>
                <Button 
                  onClick={() => navigate("/verification")}
                  className="bg-white text-[#6B4E8A] hover:bg-white/90 rounded-full text-sm"
                  data-testid="verify-banner-btn"
                >
                  Start Verification
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Quick Actions */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <EditableText
            blockKey="dashboard.quick_actions_title"
            fallback="Quick Actions"
            as="h2"
            className="text-lg font-medium text-[#6B4E8A] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {quickActions.map((action, idx) => {
              const locked = isPending && !PENDING_UNLOCKED.has(action.label);
              if (locked) {
                return (
                  <div
                    key={action.label}
                    className="bg-white border border-[#E5DFEC] rounded-2xl p-4 text-center relative opacity-60 cursor-not-allowed select-none"
                    data-testid={`quick-action-locked-${action.label.toLowerCase().replace(' ', '-')}`}
                    title="Awaiting admin approval"
                  >
                    <div
                      className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center"
                      style={{ backgroundColor: `${action.color}20` }}
                    >
                      <action.icon className="w-6 h-6" style={{ color: action.color }} />
                    </div>
                    <span className="text-sm font-medium text-[#6B4E8A]">{action.label}</span>
                    <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#6B4E8A] flex items-center justify-center" data-testid={`lock-icon-${action.label.toLowerCase().replace(' ', '-')}`}>
                      <Lock className="w-3 h-3 text-white" />
                    </span>
                  </div>
                );
              }
              return (
                <Link
                  key={action.label}
                  to={action.path}
                  className="bg-white border border-[#E5DFEC] rounded-2xl p-4 card-hover text-center relative"
                  data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}
                >
                  <div 
                    className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center"
                    style={{ backgroundColor: `${action.color}20` }}
                  >
                    <action.icon className="w-6 h-6" style={{ color: action.color }} />
                  </div>
                  <span className="text-sm font-medium text-[#6B4E8A]">{action.label}</span>
                  {action.badge > 0 && (
                    <span className="absolute top-3 right-3 w-5 h-5 bg-[#EF4444] text-white text-xs rounded-full flex items-center justify-center">
                      {action.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </motion.div>

        {/* My Regulator quick-find — auto-detects from user.province */}
        {!isPending && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mb-6"
          >
            <MyRegulatorCard user={user} />
          </motion.div>
        )}

        {/* Two Column Layout — hidden for pending members, they see the locked tiles + banner only */}
        {!isPending && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Discussions */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white border border-[#E5DFEC] rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Recent Discussions
              </h2>
              <Link to="/discussions" className="text-sm text-[#8DB5A0] hover:text-[#D0694E] flex items-center">
                View all <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-16 bg-[#F0ECF7] rounded-xl animate-pulse"></div>
                ))}
              </div>
            ) : stats.recentDiscussions.length > 0 ? (
              <div className="space-y-3">
                {stats.recentDiscussions.map((discussion) => (
                  <Link 
                    key={discussion.discussion_id}
                    to={`/discussions/${discussion.discussion_id}`}
                    className="block p-4 bg-[#FAF7F2] rounded-xl hover:bg-[#F0ECF7] transition-colors"
                  >
                    <h4 className="font-medium text-[#6B4E8A] text-sm line-clamp-1">{discussion.title}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-[#64748B]">by {discussion.author_name}</span>
                      <span className="text-xs text-[#64748B]">• {discussion.replies_count} replies</span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-[#64748B]">
                <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No discussions yet</p>
                <Button 
                  onClick={() => navigate("/discussions")}
                  variant="link"
                  className="text-[#8DB5A0] mt-2"
                >
                  Start a discussion
                </Button>
              </div>
            )}
          </motion.div>

          {/* Upcoming Events */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white border border-[#E5DFEC] rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Upcoming Sessions
              </h2>
              <Link to="/video" className="text-sm text-[#8DB5A0] hover:text-[#D0694E] flex items-center">
                View all <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-16 bg-[#F0ECF7] rounded-xl animate-pulse"></div>
                ))}
              </div>
            ) : stats.upcomingEvents.length > 0 ? (
              <div className="space-y-3">
                {stats.upcomingEvents.map((event) => (
                  <Link 
                    key={event.room_id}
                    to={`/video/${event.room_id}`}
                    className="block p-4 bg-[#FAF7F2] rounded-xl hover:bg-[#F0ECF7] transition-colors"
                  >
                    <h4 className="font-medium text-[#6B4E8A] text-sm line-clamp-1">{event.name}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-[#64748B]">by {event.host_name}</span>
                      <span className="text-xs text-[#8DB5A0]">
                        {new Date(event.scheduled_time).toLocaleDateString()}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-[#64748B]">
                <Video className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No upcoming sessions</p>
                <Button 
                  onClick={() => navigate("/video")}
                  variant="link"
                  className="text-[#8DB5A0] mt-2"
                >
                  Schedule a session
                </Button>
              </div>
            )}
          </motion.div>
        </div>
        )}

        {/* Breathing Reminder */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8 bg-gradient-to-r from-[#6B4E8A] to-[#553D6F] rounded-2xl p-6 text-white"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#F0ECF7]/20 flex items-center justify-center">
                <Wind className="w-6 h-6 text-[#F0ECF7]" />
              </div>
              <div>
                <h3 className="font-medium text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Need a break?
                </h3>
                <p className="text-white/70 text-sm">
                  Try our guided box breathing exercise
                </p>
              </div>
            </div>
            <Button 
              onClick={() => navigate("/breathing")}
              className="bg-[#F0ECF7] text-[#6B4E8A] hover:bg-[#F0ECF7]/90 rounded-full"
              data-testid="breathing-cta-btn"
            >
              Start Breathing
            </Button>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default Dashboard;
